import { useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Search, HardDrive, AlertTriangle, CheckCircle2, RefreshCw, Info } from "lucide-react";

/**
 * Admin: R2 media library — find and delete images no longer referenced anywhere
 * in Mongo. Common source of orphans: PDF page renders where Gemini only picks
 * one hero page, or AI-generated images regenerated a few times before saving.
 */
export default function AdminMedia() {
  const [stats, setStats] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [safetyHours, setSafetyHours] = useState(24);
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/media");
      setStats(data);
    } catch (e) {
      toast.error(extractError(e, "تعذّر جلب الإحصائيات"));
    }
  };

  const scan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const { data } = await api.post(`/admin/media/scan?safety_hours=${safetyHours}`);
      setScanResult(data);
      if (data.orphan_count === 0) {
        toast.success("لا توجد صور يتيمة — كل شيء نظيف!");
      } else {
        toast.info(`تم العثور على ${data.orphan_count} صورة يتيمة (${data.orphan_human})`);
      }
      loadStats();
    } catch (e) {
      toast.error(extractError(e, "فشل الفحص"));
    } finally { setScanning(false); }
  };

  const cleanup = async () => {
    if (!scanResult || scanResult.orphan_count === 0) return;
    const msg = `سيتم حذف ${scanResult.orphan_count} صورة (${scanResult.orphan_human}) من Cloudflare R2 نهائياً. لا يمكن التراجع. متأكد؟`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      // Pass the exact keys the admin just previewed — safer than re-scanning.
      const keys = scanResult.orphans.map((o) => o.key);
      const { data } = await api.post("/admin/media/cleanup", { confirm: true, keys });
      toast.success(`تم حذف ${data.deleted} صورة${data.failed ? ` (فشل ${data.failed})` : ""}`);
      setScanResult(null);
      loadStats();
    } catch (e) {
      toast.error(extractError(e, "فشل الحذف"));
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="admin-media">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-semibold flex items-center gap-2">
          <HardDrive className="h-6 w-6 text-primary" /> مكتبة الوسائط
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          فحص وحذف الصور غير المستخدمة على Cloudflare R2 — توفير مساحة وتكاليف.
        </p>
      </div>

      {/* Stats */}
      <Card className="p-5 space-y-3" data-testid="media-stats-card">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-display font-semibold">إحصائيات التخزين</h2>
          <Button size="sm" variant="ghost" onClick={loadStats} className="gap-1 rounded-full">
            <RefreshCw className="h-3.5 w-3.5" /> تحديث
          </Button>
        </div>
        {!stats ? (
          <p className="text-sm text-muted-foreground">اضغط "تحديث" لجلب الإحصائيات.</p>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
                <div className="text-xs text-muted-foreground">إجمالي الملفات</div>
                <div className="text-2xl font-display font-semibold mt-0.5">{stats.total_count.toLocaleString("ar-EG")}</div>
              </div>
              <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
                <div className="text-xs text-muted-foreground">الحجم الإجمالي</div>
                <div className="text-2xl font-display font-semibold mt-0.5">{stats.total_human}</div>
              </div>
            </div>
            <div className="rounded-lg border bg-secondary/40 divide-y">
              {stats.per_prefix.map((p) => (
                <div key={p.prefix} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`media-prefix-${p.prefix.replace("/", "")}`}>
                  <code className="text-xs bg-background rounded px-1.5 py-0.5" dir="ltr">{p.prefix}</code>
                  <div className="text-xs text-muted-foreground">
                    {p.count.toLocaleString("ar-EG")} ملف · <span className="font-medium">{p.human}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Scan & cleanup */}
      <Card className="p-5 space-y-4" data-testid="media-scan-card">
        <div>
          <h2 className="font-display font-semibold flex items-center gap-2">
            <Search className="h-4 w-4 text-primary" /> فحص الصور اليتيمة
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            يعرض كل صورة على R2 غير مرتبطة بأي وصفة، منشور، أفاتار، أو مرفق درس.
          </p>
        </div>

        <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 p-3 text-xs text-blue-900 dark:text-blue-100 flex items-start gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p><b>حماية أمان:</b> بشكل افتراضي نتجاهل الصور المرفوعة خلال آخر 24 ساعة (لتفادي حذف رفع قيد التنفيذ).</p>
            <p>خفّض القيمة إلى <b>0</b> لرؤية كل الصور اليتيمة بغض النظر عن عمرها.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">تجاهل الصور الأحدث من (ساعة)</label>
            <input
              type="number"
              min={0}
              max={720}
              value={safetyHours}
              onChange={(e) => setSafetyHours(Math.max(0, Math.min(720, parseInt(e.target.value) || 0)))}
              className="w-24 h-9 px-3 rounded-md border bg-background text-sm"
              data-testid="media-safety-hours"
            />
          </div>
          <Button onClick={scan} disabled={scanning} className="gap-2 rounded-full" data-testid="btn-media-scan">
            <Search className="h-4 w-4" /> {scanning ? "جارٍ الفحص..." : "فحص الآن"}
          </Button>
        </div>

        {scanResult && (
          <div className="space-y-3 pt-2" data-testid="media-scan-result">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border p-3 text-center">
                <div className="text-[11px] text-muted-foreground">مفحوصة</div>
                <div className="text-xl font-display font-semibold">{scanResult.scanned}</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div className="text-[11px] text-muted-foreground">مستخدمة</div>
                <div className="text-xl font-display font-semibold text-emerald-600">{scanResult.referenced}</div>
              </div>
              <div className="rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 text-center">
                <div className="text-[11px] text-amber-800 dark:text-amber-200">يتيمة</div>
                <div className="text-xl font-display font-semibold text-amber-700 dark:text-amber-300">{scanResult.orphan_count}</div>
                <div className="text-[10px] text-amber-700 dark:text-amber-400">{scanResult.orphan_human}</div>
              </div>
            </div>

            {scanResult.ignored_recent > 0 && (
              <p className="text-[11px] text-muted-foreground">
                تم تجاهل <b>{scanResult.ignored_recent}</b> صورة جديدة (أحدث من {scanResult.safety_age_hours} ساعة).
              </p>
            )}

            {scanResult.orphan_count > 0 && (
              <>
                <div className="rounded-lg border max-h-72 overflow-y-auto" data-testid="orphan-list">
                  {scanResult.orphans.slice(0, 200).map((o) => (
                    <div key={o.key} className="flex items-center gap-3 px-3 py-1.5 text-xs border-b last:border-b-0 hover:bg-secondary/40">
                      <a href={o.url} target="_blank" rel="noreferrer" className="shrink-0">
                        <img src={o.url} alt="" loading="lazy" className="h-10 w-10 object-cover rounded border" />
                      </a>
                      <code dir="ltr" className="flex-1 min-w-0 truncate text-[10px]">{o.key}</code>
                      <span className="text-muted-foreground shrink-0">{o.human}</span>
                    </div>
                  ))}
                  {scanResult.orphan_count > 200 && (
                    <div className="px-3 py-2 text-[11px] text-center text-muted-foreground">
                      + {scanResult.orphan_count - 200} صورة أخرى (ستُحذف كلها عند التأكيد)
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 items-center pt-2">
                  <Button onClick={cleanup} disabled={deleting} variant="destructive" className="gap-2 rounded-full" data-testid="btn-media-cleanup">
                    <Trash2 className="h-4 w-4" />
                    {deleting ? "جارٍ الحذف..." : `احذف ${scanResult.orphan_count} صورة (${scanResult.orphan_human})`}
                  </Button>
                  <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 text-amber-600" /> لا يمكن التراجع.
                  </div>
                </div>
              </>
            )}

            {scanResult.orphan_count === 0 && (
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 p-3 text-sm text-emerald-900 dark:text-emerald-100 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> ممتاز — لا توجد صور يتيمة تحت العتبة الحالية.
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
